// ============================================
// Driver Settings Page — Profile Management
// ============================================

import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import api from '../../services/api';
import Sidebar from '../../components/common/Sidebar';
import BottomNav from '../../components/common/BottomNav';
import DriverMobileHeader from '../../components/driver/DriverMobileHeader';
import { driverMenuItems } from './driverNav';
import { loadUser } from '../../redux/slices/authSlice';
import { useTheme } from '../../context/ThemeContext';

const Settings = () => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const { theme, toggleTheme } = useTheme();
    const [formData, setFormData] = useState({
        name: user?.name || '',
        phone: user?.phone || '',
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    // Fetch fresh profile from API on mount to reconcile phone number
    const fetchProfile = useCallback(async () => {
        try {
            const res = await api.get('/auth/profile');
            const p = res.data.data?.user;
            if (p) {
                setFormData(prev => ({
                    ...prev,
                    name: p.name || prev.name,
                    phone: p.phone || prev.phone,
                }));
            }
        } catch (err) { console.error('Settings fetch profile error:', err); }
    }, []);

    useEffect(() => { fetchProfile(); }, [fetchProfile]);
    const [deactivateModal, setDeactivateModal] = useState({ step: 0 });
    const [deactivateText, setDeactivateText] = useState('');
    const [deactivateLoading, setDeactivateLoading] = useState(false);

    // Password State
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [pwdLoading, setPwdLoading] = useState(false);
    const [pwdMessage, setPwdMessage] = useState({ type: '', text: '' });
    const [showPwd, setShowPwd] = useState(false);

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
    const handlePasswordChange = (e) => setPasswordData({ ...passwordData, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ type: '', text: '' });

        try {
            await api.patch('/driver/profile', formData);
            setMessage({ type: 'success', text: 'Profile updated successfully!' });
            dispatch(loadUser());
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.message || 'Update failed' });
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setPwdMessage({ type: '', text: '' });

        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setPwdMessage({ type: 'error', text: 'New passwords do not match' });
            return;
        }

        setPwdLoading(true);
        try {
            await api.put('/auth/change-password', {
                currentPassword: passwordData.currentPassword,
                newPassword: passwordData.newPassword
            });
            setPwdMessage({ type: 'success', text: 'Password changed successfully' });
            setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (error) {
            setPwdMessage({ type: 'error', text: error.response?.data?.message || 'Failed to change password' });
        } finally {
            setPwdLoading(false);
        }
    };

    const handleDeactivateCancel = () => { setDeactivateModal({ step: 0 }); setDeactivateText(''); };
    const handleDeactivateConfirm = async () => {
        if (deactivateText !== 'DEACTIVATE') return;
        setDeactivateLoading(true);
        try {
            await api.patch('/driver/deactivate');
            // Clear ALL tokens to prevent auto-login to any other portal
            localStorage.removeItem('driver_token');
            localStorage.removeItem('driver_refreshToken');
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('admin_token');
            localStorage.removeItem('admin_refreshToken');
            localStorage.removeItem('dispatcher_token');
            localStorage.removeItem('dispatcher_refreshToken');
            localStorage.removeItem('user');
            setMessage({ type: 'success', text: 'Account deactivated. You will be logged out.' });
            setTimeout(() => { window.dispatchEvent(new CustomEvent('auth:required', { detail: { path: '/login' } })); }, 2000);
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.message || 'Failed to deactivate' });
        } finally {
            setDeactivateLoading(false);
            handleDeactivateCancel();
        }
    };

    return (
        <div className="flex flex-col sm:flex-row h-screen bg-slate-100 dark:bg-[#050510] text-slate-900 dark:text-white font-sans transition-colors duration-500 aurora-bg relative">
            <Sidebar items={driverMenuItems} theme="teal" title="IdaCab Driver" />

            <div className="flex-1 flex flex-col overflow-hidden">
                {/* Mobile Header */}
                <DriverMobileHeader user={user} theme={theme} toggleTheme={toggleTheme} />

                {/* Desktop Header */}
                <header className="hidden sm:flex h-24 bg-white/40 dark:bg-slate-900/40 backdrop-blur-3xl items-center px-10 flex-shrink-0 z-50 relative border-b border-white/60 dark:border-white/5 shadow-sm dark:shadow-2xl transition-colors duration-500">
                    <h2 className="text-2xl font-black bg-gradient-to-r from-teal-600 to-teal-400 bg-clip-text text-transparent tracking-tighter">Account Settings</h2>
                </header>

                <main className="flex-1 overflow-y-auto px-4 pt-2 pb-28 sm:p-8 bg-transparent sidebar-scroll transition-colors duration-500">
                    <div className="max-w-2xl mx-auto space-y-8">
                        <div className="relative bg-white/70 dark:bg-slate-900/60 backdrop-blur-3xl rounded-[2.5rem] border border-white/60 dark:border-white/10 shadow-xl shadow-slate-200/50 dark:shadow-black/50 overflow-hidden group hover:shadow-2xl hover:shadow-teal-500/20 dark:hover:shadow-teal-500/10 transition-all duration-500">
                            <div className="p-8 sm:p-10 border-b border-white/60 dark:border-white/5 bg-white/40 dark:bg-slate-800/20 relative overflow-hidden transition-colors">
                                <div className="absolute -right-10 -top-10 w-32 h-32 bg-teal-50 dark:bg-teal-500/5 rounded-full opacity-40 group-hover:scale-150 transition-transform duration-700" />
                                <div className="flex flex-col sm:flex-row items-center gap-8 relative z-10">
                                    <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center text-white text-4xl font-black shadow-2xl shadow-teal-500/30 transform group-hover:rotate-3 transition-transform">
                                        {user?.name?.charAt(0)}
                                    </div>
                                    <div className="text-center sm:text-left">
                                        <h3 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight">{user?.name}</h3>
                                        <p className="text-sm text-slate-400 dark:text-slate-500 font-bold uppercase tracking-widest">{user?.email}</p>
                                        <div className="mt-4 flex flex-wrap justify-center sm:justify-start gap-2">
                                            {user?.isVerified ? (
                                                <span className="px-4 py-1.5 rounded-full bg-teal-50 dark:bg-teal-500/10 text-teal-600 dark:text-teal-400 text-[10px] font-black uppercase tracking-widest border border-teal-100 dark:border-teal-500/20 shadow-sm">
                                                    Verified Driver
                                                </span>
                                            ) : (
                                                <span className="px-4 py-1.5 rounded-full bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 text-[10px] font-black uppercase tracking-widest border border-amber-100 dark:border-amber-500/20 shadow-sm">
                                                    Unverified
                                                </span>
                                            )}
                                            <span className="px-4 py-1.5 rounded-full bg-slate-900 dark:bg-white/10 text-white text-[10px] font-black uppercase tracking-widest shadow-lg shadow-black/10">
                                                Active Status
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <form onSubmit={handleSubmit} className="p-8 sm:p-10 space-y-8">
                                {message.text && (
                                    <div className={`p-5 rounded-2xl text-xs font-black uppercase tracking-widest border animate-in fade-in slide-in-from-top-4 duration-300 ${message.type === 'success' ? 'bg-teal-50 border-teal-100 text-teal-700 shadow-sm' : 'bg-red-50 border-red-100 text-red-700 shadow-sm'}`}>
                                        <div className="flex items-center gap-3">
                                            <span className="text-xl">{message.type === 'success' ? <svg className="w-5 h-5 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg> : <svg className="w-5 h-5 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>}</span>
                                            {message.text}
                                        </div>
                                    </div>
                                )}

                                <div className="grid gap-8">
                                    <div className="space-y-3">
                                        <label className="ml-1 uppercase tracking-[0.2em] text-[10px] font-black text-slate-400 flex items-center gap-2">
                                            <span className="w-1 h-1 rounded-full bg-teal-500" /> Display Name
                                        </label>
                                        <input
                                            name="name" type="text" value={formData.name} onChange={handleChange} required
                                            className="w-full px-6 py-4 rounded-2xl bg-white/80 dark:bg-slate-800/50 border-2 border-white/80 dark:border-white/10 focus:border-teal-500 dark:focus:border-teal-400 focus:ring-4 focus:ring-teal-500/5 outline-none transition-all text-sm font-black text-slate-700 dark:text-white shadow-sm hover:border-slate-200 dark:hover:border-white/20"
                                        />
                                    </div>

                                    <div className="space-y-3">
                                        <label className="ml-1 uppercase tracking-[0.2em] text-[10px] font-black text-slate-400 flex items-center gap-2">
                                            <span className="w-1 h-1 rounded-full bg-slate-200" /> Email Address
                                        </label>
                                        <div className="relative group/input">
                                            <input
                                                value={user?.email} disabled
                                                className="w-full px-6 py-4 rounded-2xl bg-slate-50/80 dark:bg-slate-800/30 border-2 border-slate-100 dark:border-white/5 text-slate-400 dark:text-slate-500 cursor-not-allowed text-sm font-black shadow-inner"
                                            />
                                            <div className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover/input:opacity-100 transition-opacity">
                                                <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Locked</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        <label className="ml-1 uppercase tracking-[0.2em] text-[10px] font-black text-slate-400 flex items-center gap-2">
                                            <span className="w-1 h-1 rounded-full bg-teal-500" /> Mobile Number
                                        </label>
                                        <input
                                            name="phone" type="tel" value={formData.phone} onChange={handleChange} required
                                            className="w-full px-6 py-4 rounded-2xl bg-white/80 dark:bg-slate-800/50 border-2 border-white/80 dark:border-white/10 focus:border-teal-500 dark:focus:border-teal-400 focus:ring-4 focus:ring-teal-500/5 outline-none transition-all text-sm font-black text-slate-700 dark:text-white shadow-sm hover:border-slate-200 dark:hover:border-white/20"
                                        />
                                    </div>
                                </div>

                                <button type="submit" disabled={loading}
                                    className="w-full py-5 rounded-2xl bg-slate-900 hover:bg-teal-600 text-white text-xs font-black uppercase tracking-[0.2em] shadow-xl shadow-slate-900/10 hover:shadow-teal-600/20 transition-all transform active:scale-[0.98] disabled:opacity-50"
                                >
                                    {loading ? 'SYNCING DATA...' : 'UPDATE PROFILE →'}
                                </button>
                            </form>
                        </div>

                        {/* Change Password Section */}
                        <div className="relative bg-white/70 dark:bg-slate-900/60 backdrop-blur-3xl rounded-[2.5rem] border border-white/60 dark:border-white/10 shadow-xl shadow-slate-200/50 dark:shadow-black/50 overflow-hidden group hover:shadow-2xl hover:shadow-teal-500/20 dark:hover:shadow-teal-500/10 transition-all duration-500">
                            <div className="p-8 sm:p-10 border-b border-white/60 dark:border-white/5 bg-white/40 dark:bg-slate-800/20">
                                <h3 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight">Security Settings</h3>
                                <p className="text-sm text-slate-400 dark:text-slate-500 font-bold mt-1">Update your account password</p>
                            </div>
                            <form onSubmit={handlePasswordSubmit} className="p-8 sm:p-10 space-y-8">
                                {pwdMessage.text && (
                                    <div className={`p-5 rounded-2xl text-xs font-black uppercase tracking-widest border animate-in fade-in slide-in-from-top-4 duration-300 ${pwdMessage.type === 'success' ? 'bg-teal-50 border-teal-100 text-teal-700 shadow-sm' : 'bg-red-50 border-red-100 text-red-700 shadow-sm'}`}>
                                        <div className="flex items-center gap-3">
                                            <span className="text-xl">{pwdMessage.type === 'success' ? <svg className="w-5 h-5 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg> : <svg className="w-5 h-5 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>}</span>
                                            {pwdMessage.text}
                                        </div>
                                    </div>
                                )}
                                
                                <div className="grid gap-6">
                                    <div className="space-y-3">
                                        <label className="ml-1 uppercase tracking-[0.2em] text-[10px] font-black text-slate-400 flex items-center gap-2">
                                            Current Password
                                        </label>
                                        <div className="relative">
                                            <input
                                                name="currentPassword" type={showPwd ? 'text' : 'password'} value={passwordData.currentPassword} onChange={handlePasswordChange} required
                                                className="w-full px-6 py-4 rounded-2xl bg-white/80 dark:bg-slate-800/50 border-2 border-white/80 dark:border-white/10 focus:border-teal-500 dark:focus:border-teal-400 focus:ring-4 focus:ring-teal-500/5 outline-none transition-all text-sm font-black text-slate-700 dark:text-white shadow-sm hover:border-slate-200 dark:hover:border-white/20"
                                                placeholder="Enter current password"
                                            />
                                            <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute inset-y-0 right-0 pr-5 flex items-center text-[10px] font-black text-slate-400 uppercase hover:text-teal-500 tracking-widest">
                                                {showPwd ? 'Hide' : 'Show'}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        <label className="ml-1 uppercase tracking-[0.2em] text-[10px] font-black text-slate-400 flex items-center gap-2">
                                            New Password
                                        </label>
                                        <input
                                            name="newPassword" type={showPwd ? 'text' : 'password'} value={passwordData.newPassword} onChange={handlePasswordChange} required minLength={8}
                                            className="w-full px-6 py-4 rounded-2xl bg-white/80 dark:bg-slate-800/50 border-2 border-white/80 dark:border-white/10 focus:border-teal-500 dark:focus:border-teal-400 focus:ring-4 focus:ring-teal-500/5 outline-none transition-all text-sm font-black text-slate-700 dark:text-white shadow-sm hover:border-slate-200 dark:hover:border-white/20"
                                            placeholder="••••••••"
                                        />
                                    </div>

                                    <div className="space-y-3">
                                        <label className="ml-1 uppercase tracking-[0.2em] text-[10px] font-black text-slate-400 flex items-center gap-2">
                                            Confirm New Password
                                        </label>
                                        <input
                                            name="confirmPassword" type={showPwd ? 'text' : 'password'} value={passwordData.confirmPassword} onChange={handlePasswordChange} required minLength={8}
                                            className="w-full px-6 py-4 rounded-2xl bg-white/80 dark:bg-slate-800/50 border-2 border-white/80 dark:border-white/10 focus:border-teal-500 dark:focus:border-teal-400 focus:ring-4 focus:ring-teal-500/5 outline-none transition-all text-sm font-black text-slate-700 dark:text-white shadow-sm hover:border-slate-200 dark:hover:border-white/20"
                                            placeholder="••••••••"
                                        />
                                    </div>
                                </div>

                                <button type="submit" disabled={pwdLoading}
                                    className="w-full py-5 rounded-2xl bg-slate-900 hover:bg-teal-600 text-white text-xs font-black uppercase tracking-[0.2em] shadow-xl shadow-slate-900/10 hover:shadow-teal-600/20 transition-all transform active:scale-[0.98] disabled:opacity-50"
                                >
                                    {pwdLoading ? 'UPDATING...' : 'CHANGE PASSWORD →'}
                                </button>
                            </form>
                        </div>

                        {/* Account Deactivation */}
                        <div className="p-8 relative bg-red-50/80 dark:bg-red-950/30 backdrop-blur-xl rounded-[2.5rem] border border-red-100 dark:border-red-500/20 flex flex-col sm:flex-row items-center justify-between gap-6 group hover:bg-red-100/50 dark:hover:bg-red-950/40 transition-colors">
                            <div className="text-center sm:text-left">
                                <h4 className="text-red-900 dark:text-red-400 font-black text-sm uppercase tracking-tight mb-1">Deactivate Account</h4>
                                <p className="text-red-600 dark:text-red-500 text-[10px] font-black uppercase tracking-widest opacity-80">Temporarily deactivate your driver account</p>
                            </div>
                            <button 
                                onClick={() => setDeactivateModal({ step: 1 })}
                                className="w-full sm:w-auto px-10 py-3.5 rounded-2xl border-2 border-red-200 dark:border-red-500/20 bg-white dark:bg-red-950/30 text-red-600 dark:text-red-400 text-[10px] font-black uppercase tracking-widest hover:bg-red-600 hover:text-white hover:border-red-600 dark:hover:bg-red-600 dark:hover:text-white dark:hover:border-red-600 transition-all shadow-lg shadow-red-500/5"
                            >
                                DEACTIVATE
                            </button>
                        </div>
                    </div>
                </main>
            </div>

            <BottomNav items={driverMenuItems} />

            {/* Deactivate Confirmation Modal — Step 1 */}
            {deactivateModal.step === 1 && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-slate-900/60 dark:bg-black/60 backdrop-blur-sm" onClick={handleDeactivateCancel} />
                    <div className="relative bg-white/90 dark:bg-slate-900/90 backdrop-blur-3xl rounded-[2.5rem] shadow-2xl dark:shadow-black/50 w-full max-w-sm overflow-hidden border border-white/60 dark:border-white/10">
                        <div className="p-8 text-center">
                            <div className="w-16 h-16 bg-rose-50 dark:bg-rose-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-inner border border-rose-100 dark:border-rose-500/20">
                                <svg className="w-8 h-8 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                            </div>
                            <h3 className="text-xl font-black text-slate-900 dark:text-white mb-2 uppercase tracking-tight">Deactivate Account?</h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400 font-medium leading-relaxed mb-6">
                                Are you sure you want to deactivate your driver account? You will lose access to all routes, jobs, and earnings.
                            </p>
                            <div className="grid grid-cols-2 gap-3">
                                <button onClick={handleDeactivateCancel} className="px-4 py-3.5 rounded-xl text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800/50 hover:bg-slate-200 dark:hover:bg-slate-700/50 font-bold text-sm tracking-wide transition-colors border border-transparent dark:border-white/5">
                                    Cancel
                                </button>
                                <button onClick={() => { setDeactivateText(''); setDeactivateModal({ step: 2 }); }} className="px-4 py-3.5 rounded-xl text-white bg-rose-600 hover:bg-rose-700 shadow-lg shadow-rose-600/30 font-bold text-sm tracking-wide transition-all transform active:scale-95">
                                    Yes, Continue
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Deactivate Confirmation Modal — Step 2: Type DEACTIVATE */}
            {deactivateModal.step === 2 && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-slate-900/60 dark:bg-black/60 backdrop-blur-sm" onClick={handleDeactivateCancel} />
                    <div className="relative bg-white/90 dark:bg-slate-900/90 backdrop-blur-3xl rounded-[2.5rem] shadow-2xl dark:shadow-black/50 w-full max-w-sm overflow-hidden border border-white/60 dark:border-white/10">
                        <div className="p-8 text-center">
                            <div className="w-16 h-16 bg-red-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl shadow-red-600/30">
                                <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                            </div>
                            <h3 className="text-xl font-black text-slate-900 dark:text-white mb-2 uppercase tracking-tight">Final Confirmation</h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400 font-medium leading-relaxed mb-4">
                                Type <span className="font-black text-red-600 dark:text-red-400">DEACTIVATE</span> below to confirm.
                            </p>
                            <input
                                type="text"
                                value={deactivateText}
                                onChange={(e) => setDeactivateText(e.target.value.toUpperCase())}
                                placeholder="Type DEACTIVATE"
                                className="w-full px-5 py-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border-2 border-slate-200 dark:border-slate-700 text-center text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest focus:border-red-500 focus:ring-4 focus:ring-red-500/10 outline-none transition-all mb-6"
                            />
                            <div className="grid grid-cols-2 gap-3">
                                <button onClick={handleDeactivateCancel} className="px-4 py-3.5 rounded-xl text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800/50 hover:bg-slate-200 dark:hover:bg-slate-700/50 font-bold text-sm tracking-wide transition-colors border border-transparent dark:border-white/5">
                                    Cancel
                                </button>
                                <button 
                                    onClick={handleDeactivateConfirm}
                                    disabled={deactivateText !== 'DEACTIVATE' || deactivateLoading}
                                    className="px-4 py-3.5 rounded-xl text-white bg-red-600 hover:bg-red-700 shadow-lg shadow-red-600/30 font-bold text-sm tracking-wide transition-all transform active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    {deactivateLoading ? 'Processing...' : 'Deactivate'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Settings;
